import React, {
  useEffect,
} from 'react';
import Dialog from 'pages/Dashboard/components/Dialog';
import Button from 'pages/Dashboard/components/Button';
import Input from 'pages/Dashboard/components/Input';
import {
  useAddContact,
  useUpdateContact,
} from 'pages/Dashboard/pages/Fax/hooks';
import {
  useForm,
} from 'react-hook-form';
import Controller from 'core/components/FormController';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import PhoneInput from 'core/components/PhoneInput';
import isNil from 'lodash/isNil';
import {
  normalizePhoneNumber,
  validatePhone,
} from 'pages/Dashboard/utils/helper';
import {
  faxEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  useNotify,
} from 'core/hooks';
import {
  FaxContactDTO,
} from 'dtos';

export type AddContactFormParams = {
  defaultPhoneNumber?: string;
};

type ContactsFormProps = AddContactFormParams & {
  open: boolean;
  onAdd?: (contact: FaxContactDTO) => void;
  onClose: () => void;
  contact?: FaxContactDTO;
};

type ContactCreateData = {
  name: string;
  phoneNumber: string;
};

export default function ContactsForm({
  open,
  onClose,
  onAdd,
  defaultPhoneNumber,
  contact,
}: ContactsFormProps) {
  const {
    mutateAsync: addContact,
    isSuccess: isSuccessAddContact,
    isPending: isLoadingAddContact,
  } = useAddContact();
  const {
    mutateAsync: updateContact,
    isSuccess: isSuccessUpdateContact,
    isPending: isLoadingUpdateContact,
  } = useUpdateContact();
  const notify = useNotify();
  const hasContact = !isNil(contact);
  const title = `${hasContact ? 'Edit' : 'Add'} Contact`;
  const confirmText = hasContact ? 'Update' : 'Create';
  const successMessage = `Contact ${hasContact ? 'Updated' : 'Added'} Successfully`;

  const {
    handleSubmit,
    control,
    formState: { isValid },
  } = useForm<ContactCreateData>({
    defaultValues: {
      name: contact?.name ?? '',
      phoneNumber: contact?.faxNumber ?? defaultPhoneNumber,
    },
  });

  useEffect(() => {
    if (isSuccessUpdateContact || isSuccessAddContact) {
      notify({
        variant: 'success',
        message: successMessage,
      });
      onClose();
      faxEventChannel.emit('onContactChange');
    }
  }, [isSuccessUpdateContact, isSuccessAddContact]);

  const onSubmit = async (data: ContactCreateData) => {
    const { name, phoneNumber } = data;
    const payload = {
      name,
      faxNumber: normalizePhoneNumber(phoneNumber),
    };
    if (hasContact) {
      const updatePayload = {
        ...contact,
        ...payload,
      };
      updateContact(updatePayload);
    } else {
      const newContact = await addContact(payload);
      onAdd?.(newContact);
    }
  };

  return (
    <Dialog
      open={open}
      title={title}
      onClose={onClose}
      onConfirm={() => {}}
    >
      <div className="flex flex-col gap-form-row">
        <Controller
          name="phoneNumber"
          control={control}
          rules={{ required: true, validate: validatePhone }}
          render={({ field, fieldState: { error } }) => (
            <Wrapper
              id="fax-number"
              error={!isNil(error)}
              showErrorText={false}
              label="Fax Number"
              labelClasses="font-semibold text-xs text-gray-500"
              required
            >
              <PhoneInput
                {...field}
                required
                error={!isNil(error)}
                id="fax-number"
                name="fax-number"
              />
            </Wrapper>
          )}
        />
        <Controller
          name="name"
          control={control}
          rules={{ required: true, maxLength: 40 }}
          render={
            ({ field }) => (
              <Input
                {...field}
                required
                labelClasses="font-semibold text-xs text-gray-500"
                label="Name"
                inputProps={{ maxLength: 40 }}
                allowSpeechToText
                placeholder="Enter contact name here"
                id="name"
                trackingLabel="Name"
              />
            )
          }
        />
      </div>
      <div className="flex flex-row-reverse justify-between">
        <Button
          className="!w-28"
          variant="contained"
          color="primary"
          type="submit"
          disabled={!isValid || isLoadingAddContact || isLoadingUpdateContact}
          onClick={handleSubmit(onSubmit)}
        >
          {confirmText}
        </Button>
        <Button
          variant="outlined"
          type="button"
          onClick={onClose}
        >
          Close
        </Button>
      </div>
    </Dialog>
  );
}
